<!-- 笔记对话框组件 -->
<template>
  <el-dialog
    v-model="dialogVisible"
    :close-on-click-modal="false"
    class="notes-dialog"
    :draggable="true"
    width="80%"
  >
    <!-- 自定义标题栏区域 -->
    <template #header>
      <div class="custom-header">
        <div class="dialog-title">{{ title }}</div>
        <div class="format-buttons">
          <div 
            class="format-button" 
            :class="{ 'active': currentFormat === 'h1' }" 
            @click="applyFormat('h1')"
          >H1</div>
          <div 
            class="format-button" 
            :class="{ 'active': currentFormat === 'h2' }" 
            @click="applyFormat('h2')"
          >H2</div>
          <div 
            class="format-button" 
            :class="{ 'active': currentFormat === 'h3' }" 
            @click="applyFormat('h3')"
          >H3</div>
          <div 
            class="format-button" 
            :class="{ 'active': currentFormat === 'p' }" 
            @click="applyFormat('p')"
          >正文</div>
          
          <!-- 高亮功能按钮组 -->
          <div class="highlight-controls">
            <div class="format-button highlight-button" title="红色高亮" @click="applyHighlight('red')">
              <div class="color-box red"></div>
            </div>
            <div class="format-button highlight-button" title="黄色高亮" @click="applyHighlight('yellow')">
              <div class="color-box yellow"></div>
            </div>
            <div class="format-button highlight-button" title="绿色高亮" @click="applyHighlight('green')">
              <div class="color-box green"></div>
            </div>
            <div class="format-button highlight-button" title="取消高亮" @click="removeHighlight()">
              <div class="color-box clear">×</div>
            </div>
          </div>
        </div>
      </div>
    </template>
    
    <div class="notes-wrapper">
      <!-- 左侧大纲区域 -->
      <div :class="['sidebar-container', {'collapsed': sidebarCollapsed}]">
        <!-- 折叠状态下的左侧占位 -->
        <div v-if="sidebarCollapsed" class="sidebar-placeholder">
          <!-- 折叠状态下的展开按钮 -->
          <div class="sidebar-toggle" @click="toggleSidebar">
            <el-icon class="toggle-icon"><ArrowRight /></el-icon>
          </div>
        </div>
        
        <div v-if="!sidebarCollapsed" class="notes-sidebar">
          <div class="sidebar-header">
            <span>大纲</span>
          </div>
          <div class="outline-view" v-if="hierarchicalOutline.length > 0">
            <div 
              v-for="(item, index) in hierarchicalOutline" 
              :key="index"
              class="outline-item-container"
            >
              <div 
                class="outline-item level-1"
                @click="scrollToHeading(item.id)"
              >
                <span class="expand-icon" @click.stop="toggleHeading(item.id)">
                  <el-icon v-if="item.children && item.children.length > 0">
                    <component :is="collapsedHeadings.includes(item.id) ? 'ArrowRight' : 'ArrowDown'" />
                  </el-icon>
                  <span v-else class="no-children-space"></span>
                </span>
                <span class="item-text">{{ item.text }}</span>
              </div>
              
              <!-- 二级标题 -->
              <div 
                v-if="item.children && item.children.length > 0 && !collapsedHeadings.includes(item.id)"
                class="children-container"
              >
                <div 
                  v-for="(child, childIndex) in item.children" 
                  :key="`${index}-${childIndex}`"
                  class="outline-item-container child-container"
                >
                  <div 
                    class="outline-item level-2"
                    @click="scrollToHeading(child.id)"
                  >
                    <span class="expand-icon" @click.stop="toggleHeading(child.id)">
                      <el-icon v-if="child.children && child.children.length > 0">
                        <component :is="collapsedHeadings.includes(child.id) ? 'ArrowRight' : 'ArrowDown'" />
                      </el-icon>
                      <span v-else class="no-children-space"></span>
                    </span>
                    <span class="item-text">{{ child.text }}</span>
                  </div>
                  
                  <!-- 三级标题 -->
                  <div 
                    v-if="child.children && child.children.length > 0 && !collapsedHeadings.includes(child.id)"
                    class="children-container"
                  >
                    <div 
                      v-for="(grandChild, grandChildIndex) in child.children" 
                      :key="`${index}-${childIndex}-${grandChildIndex}`"
                      class="outline-item level-3"
                      @click="scrollToHeading(grandChild.id)"
                    >
                      <span class="indent-space"></span>
                      <span class="item-text">{{ grandChild.text }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-outline">
            <div class="empty-outline-content">
              <p>无大纲内容</p>
              <p class="outline-tip">添加标题后将自动生成大纲</p>
            </div>
          </div>
          
          <!-- 展开状态下的折叠按钮 -->
          <div class="sidebar-toggle-expanded" @click="toggleSidebar">
            <el-icon class="toggle-icon"><ArrowLeft /></el-icon>
          </div>
        </div>
      </div>
      
      <!-- 右侧笔记内容区域 -->
      <div class="notes-content">
        <!-- 富文本编辑区 -->
        <div 
          class="editor-content" 
          contenteditable="true" 
          ref="editorRef"
          @input="handleContentChange"
          @mouseup="handleSelectionChange"
          @keyup="handleSelectionChange"
        ></div>
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveNotes">保存</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight, ArrowDown } from '@element-plus/icons-vue'
import { getSupabase } from '@/config/localStorage'

export default {
  name: 'NotesDialog',
  components: {
    ArrowLeft,
    ArrowRight,
    ArrowDown
  },
  setup() {
    const store = useStore()
    const editorRef = ref(null)
    const sidebarCollapsed = ref(false)
    const currentFormat = ref('p')
    const outlineItems = ref([])
    const collapsedHeadings = ref([]) // 存储折叠的标题ID
    const MAX_CONTENT_LENGTH = 0.4 * 1024 * 1024 // 0.4MB大小限制
    const currentContentLength = ref(0) // 当前内容长度
    
    // 为高亮功能添加的变量
    const selectedTextRange = ref(null)
    
    // 在组件挂载时获取supabase实例
    onMounted(async () => {
      try {
        // 初始化编辑器样式
        ensureEditorStyles()
        
        // 组件挂载后自动获取折叠状态
        const savedSidebarState = localStorage.getItem(`notes_sidebar_collapsed_${title.value}`)
        if (savedSidebarState !== null) {
          sidebarCollapsed.value = savedSidebarState === 'true'
        }
        
        // 恢复标题折叠状态
        const savedCollapsedHeadings = localStorage.getItem(`notes_collapsed_headings_${title.value}`)
        if (savedCollapsedHeadings) {
          try {
            collapsedHeadings.value = JSON.parse(savedCollapsedHeadings)
          } catch (e) {
            collapsedHeadings.value = []
          }
        }
        
        // 初始化内容长度
        if (editorRef.value) {
          currentContentLength.value = editorRef.value.innerHTML.length
        }
      } catch (error) {
        console.error('初始化笔记组件失败:', error)
        ElMessage.error('初始化笔记失败，将使用本地存储模式')
      }
    })
    
    // 对话框状态
    const dialogVisible = computed({
      get: () => store.state.noteDialogVisible,
      set: (value) => store.commit('setNoteDialogVisible', value)
    })
    
    // 对话框标题
    const title = computed(() => store.state.noteDialogTitle)
    
    // 获取选中的文本
    const selectedText = computed(() => store.state.selectedText)
    
    // 监听对话框可见性变化
    watch(() => dialogVisible.value, (newVal, oldVal) => {
      if (oldVal && !newVal) {
        // 对话框从打开到关闭时，保存内容
        saveCurrentNotes()
      } else if (newVal) {
        // 对话框从关闭到打开时
        // 保存当前选中的文本以便后续使用
        const textToInsert = selectedText.value
        
        // 加载笔记内容
        nextTick(() => {
          loadNotes(() => {
            // 加载完成后的回调，如果有选中文本，则插入
            if (textToInsert) {
              setTimeout(() => {
                insertText(textToInsert)
                // 清空存储的选中文本，避免重复插入
                store.commit('setSelectedText', '')
              }, 100) // 增加延迟确保笔记内容已加载
            }
          })
        })
      }
    })
    
    // 监听标题变化，当笔记类型切换时加载对应笔记
    watch(() => title.value, (newTitle, oldTitle) => {
      if (newTitle && newTitle !== oldTitle && dialogVisible.value) {
        nextTick(() => {
          loadNotes()
        })
      }
    })
    
    // 折叠/展开侧边栏
    const toggleSidebar = () => {
      sidebarCollapsed.value = !sidebarCollapsed.value
      // 保存折叠状态到本地
      localStorage.setItem(`notes_sidebar_collapsed_${title.value}`, sidebarCollapsed.value)
    }

    // 折叠/展开标题
    const toggleHeading = (id) => {
      if (collapsedHeadings.value.includes(id)) {
        collapsedHeadings.value = collapsedHeadings.value.filter(headingId => headingId !== id)
      } else {
        collapsedHeadings.value.push(id)
      }
      // 保存折叠状态到本地
      localStorage.setItem(`notes_collapsed_headings_${title.value}`, JSON.stringify(collapsedHeadings.value))
    }
    
    // 接收外部传入的文本并插入到编辑器
    const insertText = (text) => {
      if (!editorRef.value) return
      
      // 检查插入后是否会超过限制
      const estimatedLength = (editorRef.value.innerHTML.length) + text.length + 7 // 7是<p></p>标签的长度
      if (estimatedLength > MAX_CONTENT_LENGTH) {
        ElMessage.warning(`插入内容后将超出限制，无法插入全部内容`)
        // 计算可以插入的最大长度
        const availableSpace = MAX_CONTENT_LENGTH - editorRef.value.innerHTML.length - 7
        if (availableSpace > 0) {
          text = text.substring(0, availableSpace)
        } else {
          ElMessage.error('笔记已达到内容上限，无法继续添加内容')
          return
        }
      }
      
      // 首先确保当前类型的笔记内容已加载
      const key = `notes_content_${title.value}`
      const savedContent = localStorage.getItem(key)
      
      if (savedContent && editorRef.value.innerHTML.trim() === '') {
        // 如果localStorage中有内容但编辑器为空，先加载内容
        editorRef.value.innerHTML = savedContent
      }
      
      // 如果编辑器为空，直接插入段落
      if (editorRef.value.innerHTML.trim() === '') {
        editorRef.value.innerHTML = `<p>${text}</p>`
      } else {
        // 始终在编辑器内容顶部插入新段落
        const p = document.createElement('p')
        p.textContent = text
        
        // 获取编辑器的第一个子元素
        const firstChild = editorRef.value.firstChild
        
        // 在第一个子元素之前插入新段落
        editorRef.value.insertBefore(p, firstChild)
        
        // 将光标移动到新插入的段落之后
        const selection = window.getSelection()
        const range = document.createRange()
        range.setStartAfter(p)
        range.setEndAfter(p)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      
      // 更新大纲
      updateOutline()
      
      // 保存当前内容
      saveCurrentNotes()
      
      // 更新内容长度
      currentContentLength.value = editorRef.value.innerHTML.length
    }
    
    // 解析编辑器内容生成大纲
    const updateOutline = () => {
      if (!editorRef.value) return
      
      const headings = editorRef.value.querySelectorAll('h1, h2, h3')
      outlineItems.value = []
      
      headings.forEach((heading, index) => {
        // 为每个标题添加ID以便于定位
        const id = `heading-${index}`
        heading.id = id
        
        outlineItems.value.push({
          id,
          text: heading.textContent,
          level: parseInt(heading.tagName.substring(1))
        })
      })
    }

    // 将平面大纲转换为层次结构
    const hierarchicalOutline = computed(() => {
      const result = []
      const h2Map = new Map()
      const h3Map = new Map()
      
      outlineItems.value.forEach(item => {
        if (item.level === 1) {
          const h1Item = { ...item, children: [] }
          result.push(h1Item)
          h2Map.set(h1Item.id, h1Item)
        } else if (item.level === 2) {
          const h2Item = { ...item, children: [] }
          
          // 查找最近的h1作为父节点
          let parentFound = false
          for (let i = outlineItems.value.indexOf(item) - 1; i >= 0; i--) {
            if (outlineItems.value[i].level === 1) {
              const parentId = outlineItems.value[i].id
              const parent = h2Map.get(parentId)
              if (parent) {
                parent.children.push(h2Item)
                h3Map.set(h2Item.id, h2Item)
                parentFound = true
                break
              }
            }
          }
          
          // 如果没有找到父节点，添加到顶层
          if (!parentFound) {
            result.push(h2Item)
            h3Map.set(h2Item.id, h2Item)
          }
        } else if (item.level === 3) {
          // 查找最近的h2作为父节点
          let parentFound = false
          for (let i = outlineItems.value.indexOf(item) - 1; i >= 0; i--) {
            if (outlineItems.value[i].level === 2) {
              const parentId = outlineItems.value[i].id
              const parent = h3Map.get(parentId)
              if (parent) {
                parent.children.push(item)
                parentFound = true
                break
              }
            }
          }
          
          // 如果没有找到父节点，查找最近的h1
          if (!parentFound) {
            for (let i = outlineItems.value.indexOf(item) - 1; i >= 0; i--) {
              if (outlineItems.value[i].level === 1) {
                const parentId = outlineItems.value[i].id
                const parent = h2Map.get(parentId)
                if (parent) {
                  parent.children.push(item)
                  parentFound = true
                  break
                }
              }
            }
          }
          
          // 如果仍然没有找到父节点，添加到顶层
          if (!parentFound) {
            result.push(item)
          }
        }
      })
      
      return result
    })
    
    // 滚动到指定标题位置
    const scrollToHeading = (id) => {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    
    // 处理内容变化
    const handleContentChange = () => {
      if (!editorRef.value) return
      
      // 获取当前内容长度
      const content = editorRef.value.innerHTML
      currentContentLength.value = content.length
      
      // 如果超出限制，裁剪内容
      if (currentContentLength.value > MAX_CONTENT_LENGTH) {
        ElMessage.warning(`内容超出限制，多余内容将被裁剪`)
        // 裁剪HTML内容需要保持HTML结构完整，所以这里简单处理
        editorRef.value.innerHTML = content.substring(0, MAX_CONTENT_LENGTH)
        currentContentLength.value = editorRef.value.innerHTML.length
      }
      
      // 更新大纲
      updateOutline()
      
      // 保存当前内容到localStorage
      saveCurrentNotes()
    }
    
    // 处理选择变化
    const handleSelectionChange = () => {
      // 获取当前选中的元素以确定活动的格式
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        const parentElement = range.commonAncestorContainer.parentElement || range.commonAncestorContainer
        
        // 检测当前激活的格式
        if (parentElement.closest('h1')) {
          currentFormat.value = 'h1'
        } else if (parentElement.closest('h2')) {
          currentFormat.value = 'h2'
        } else if (parentElement.closest('h3')) {
          currentFormat.value = 'h3'
        } else {
          currentFormat.value = 'p'
        }
        
        // 保存当前选择范围用于高亮功能 - 增强版本
        const selectedText = selection.toString().trim()
        if (selectedText) {
          // 保存选择范围的克隆版本
          selectedTextRange.value = range.cloneRange()
          console.log('已保存选择范围:', selectedText)
        }
      }
    }
    
    // 应用格式
    const applyFormat = (format) => {
      const selection = window.getSelection()
      if (selection.rangeCount === 0) return
      
      const range = selection.getRangeAt(0)
      const selectedText = range.toString().trim()
      
      if (!selectedText && format !== 'p') {
        ElMessage.warning('请先选择文本')
        return
      }
      
      // 应用格式
      document.execCommand('formatBlock', false, `<${format}>`)
      currentFormat.value = format
      
      // 编辑器中特定元素上添加额外的内联样式，确保颜色能显示
      if (editorRef.value) {
        nextTick(() => {
          // 查找所有指定标签并应用强制样式
          const applyStyleToTags = (tagName, color) => {
            const tags = editorRef.value.querySelectorAll(tagName)
            tags.forEach(tag => {
              // 直接设置内联样式
              tag.style.color = color
              tag.style.fontWeight = 'bold'
              
              // 如果是H1，添加底部边框
              if (tagName === 'h1') {
                tag.style.borderBottom = '1px solid #eee'
                tag.style.paddingBottom = '5px'
              }
            })
          }
          
          // 应用样式到各级标题
          applyStyleToTags('h1', '#409EFF') // 蓝色
          applyStyleToTags('h2', '#67C23A') // 绿色
          applyStyleToTags('h3', '#E6A23C') // 黄色
          
          // 更新大纲
          updateOutline()
        })
      }
    }
    
    // 应用高亮功能 - 改进版
    const applyHighlight = (color) => {
      // 先检查当前是否有活跃的选择
      const currentSelection = window.getSelection()
      let rangeToUse = null
      
      // 如果当前有选择，优先使用当前选择
      if (currentSelection.rangeCount > 0 && currentSelection.toString().trim()) {
        rangeToUse = currentSelection.getRangeAt(0)
        console.log('使用当前选择:', currentSelection.toString().trim())
      } 
      // 如果没有当前选择，但有保存的选择，使用保存的选择
      else if (selectedTextRange.value) {
        rangeToUse = selectedTextRange.value
        console.log('使用保存的选择:', rangeToUse.toString().trim())
      } else {
        ElMessage.warning('请先选择要高亮的文本')
        return
      }
      
      try {
        // 检查选中文本是否为空
        const selectedText = rangeToUse.toString().trim()
        if (!selectedText) {
          ElMessage.warning('请先选择要高亮的文本')
          return
        }
        
        // 创建高亮元素
        const span = document.createElement('span')
        span.className = `highlight-${color}`
        
        try {
          // 将选中内容放入高亮元素
          rangeToUse.surroundContents(span)
        } catch (err) {
          // 如果surroundContents失败（可能是跨越多个元素），改用替换方法
          console.error('使用surroundContents高亮失败:', err)
          
          // 尝试使用其他方法
          try {
            // 获取选中的内容
            const fragment = rangeToUse.extractContents()
            // 将内容放入span
            span.appendChild(fragment)
            // 将span插入到范围的开始处
            rangeToUse.insertNode(span)
          } catch (extractErr) {
            console.error('提取内容失败:', extractErr)
            
            // 如果仍然失败，尝试最简单的方法
            window.getSelection().removeAllRanges()
            ElMessage.error('应用高亮失败，请重新选择文本并尝试')
            return
          }
        }
        
        // 更新内容并触发保存
        handleContentChange()
        
        // 清除保存的选择
        selectedTextRange.value = null
        window.getSelection().removeAllRanges()
        
        ElMessage.success('已添加高亮')
      } catch (error) {
        console.error('应用高亮失败:', error)
        ElMessage.error('应用高亮失败，请重新选择文本')
      }
    }
    
    // 移除高亮
    const removeHighlight = () => {
      const selection = window.getSelection()
      if (!selection.rangeCount) {
        ElMessage.warning('请先选择要取消高亮的文本')
        return
      }
      
      try {
        const range = selection.getRangeAt(0)
        const commonAncestor = range.commonAncestorContainer
        
        // 查找可能的高亮元素
        let highlightElement = null
        
        // 如果直接选中了一个元素节点
        if (commonAncestor.nodeType === 1) {
          if (commonAncestor.classList && 
             (commonAncestor.classList.contains('highlight-red') || 
              commonAncestor.classList.contains('highlight-yellow') ||
              commonAncestor.classList.contains('highlight-green'))) {
            highlightElement = commonAncestor
          }
        }
        
        // 如果选中的是文本节点，查找父节点
        if (!highlightElement && commonAncestor.nodeType === 3 && commonAncestor.parentNode) {
          const parent = commonAncestor.parentNode
          if (parent.classList && 
             (parent.classList.contains('highlight-red') || 
              parent.classList.contains('highlight-yellow') ||
              parent.classList.contains('highlight-green'))) {
            highlightElement = parent
          }
        }
        
        // 如果找到了高亮元素
        if (highlightElement) {
          // 获取高亮元素的文本内容
          const textContent = highlightElement.textContent
          
          // 创建文本节点
          const textNode = document.createTextNode(textContent)
          
          // 替换高亮元素
          highlightElement.parentNode.replaceChild(textNode, highlightElement)
          
          // 清除选择
          selection.removeAllRanges()
          
          // 更新内容和大纲
          handleContentChange()
          
          ElMessage.success('已取消高亮')
        } else {
          // 查找选择范围内所有的高亮元素
          if (editorRef.value) {
            const highlights = editorRef.value.querySelectorAll('.highlight-red, .highlight-yellow, .highlight-green')
            let hasRemoved = false
            
            highlights.forEach(highlight => {
              // 检查是否与选择范围有交集
              if (range.intersectsNode(highlight)) {
                // 创建文本节点替换高亮元素
                const textNode = document.createTextNode(highlight.textContent)
                highlight.parentNode.replaceChild(textNode, highlight)
                hasRemoved = true
              }
            })
            
            if (hasRemoved) {
              // 更新内容和大纲
              handleContentChange()
              ElMessage.success('已取消高亮')
            } else {
              ElMessage.warning('所选内容不包含高亮标记')
            }
          }
        }
      } catch (error) {
        console.error('移除高亮失败:', error)
        ElMessage.error('移除高亮失败')
      }
    }
    
    // 保存当前笔记内容
    const saveCurrentNotes = async () => {
      if (!editorRef.value) return
      
      const content = editorRef.value.innerHTML
      console.log(`正在保存 ${title.value} 笔记，内容长度：${content.length}/${MAX_CONTENT_LENGTH} 字节`)
      
      // 检查内容是否超过限制
      if (content.length > MAX_CONTENT_LENGTH) {
        ElMessage.warning(`笔记内容超出限制，多余内容将被裁剪`)
        editorRef.value.innerHTML = content.substring(0, MAX_CONTENT_LENGTH)
        return
      }
      
      // 保存到本地存储
      const localKey = `notes_content_${title.value}`
      localStorage.setItem(localKey, content)
      
      // 使用本地存储服务保存
      try {
        const supabase = await getSupabase()
        await supabase.saveNotes(title.value, content)
        console.log(`${title.value} 笔记已成功保存到本地存储`)
            return true
          } catch (error) {
            console.error('保存笔记失败:', error)
        ElMessage.warning('保存到本地存储失败')
          return false
        }
      }
      
    // 加载当前笔记内容
    const loadCurrentNotes = async () => {
      if (!editorRef.value) return Promise.resolve()
      
        try {
        // 使用本地存储服务加载笔记
        const supabase = await getSupabase()
        const { data, error } = await supabase.loadNotes(title.value)
              
            if (error) {
          console.error('从本地存储加载笔记失败:', error)
            } else if (data && data.content) {
              // 加载数据成功
          console.log(`从本地存储加载 ${title.value} 笔记，内容长度：${data.content.length}`)
              editorRef.value.innerHTML = data.content
              await nextTick()
              updateOutline()
              if (editorRef.value) {
                currentContentLength.value = editorRef.value.innerHTML.length
              }
              return Promise.resolve()
            }
          } catch (error) {
            console.error('加载笔记时出错:', error)
      }
      
      // 如果从本地存储服务加载失败，尝试从localStorage加载
      const localKey = `notes_content_${title.value}`
      const savedContent = localStorage.getItem(localKey)
      
      console.log(`尝试从本地加载 ${title.value} 笔记，内容长度：${savedContent ? savedContent.length : 0}`)
      
      if (savedContent) {
        editorRef.value.innerHTML = savedContent
        await nextTick()
        updateOutline()
        if (editorRef.value) {
          currentContentLength.value = editorRef.value.innerHTML.length
        }
        return Promise.resolve()
      } else {
        editorRef.value.innerHTML = ''
        outlineItems.value = []
        return Promise.resolve()
      }
    }
    
    // 保存笔记
    const saveNotes = async () => {
      try {
        const success = await saveCurrentNotes()
        if (success) {
          ElMessage.success('笔记已保存')
        }
      } catch (error) {
        console.error('保存笔记时出错:', error)
        ElMessage.error('保存失败，请稍后重试')
      }
    }
    
    // 修改loadNotes方法，添加回调函数参数
    const loadNotes = (callback) => {
      // 恢复侧边栏折叠状态
      const savedSidebarState = localStorage.getItem(`notes_sidebar_collapsed_${title.value}`)
      if (savedSidebarState !== null) {
        sidebarCollapsed.value = savedSidebarState === 'true'
      }
      
      // 恢复标题折叠状态
      const savedCollapsedHeadings = localStorage.getItem(`notes_collapsed_headings_${title.value}`)
      if (savedCollapsedHeadings) {
        try {
          collapsedHeadings.value = JSON.parse(savedCollapsedHeadings)
        } catch (e) {
          collapsedHeadings.value = []
        }
      }
      
      // 加载笔记内容
      nextTick(() => {
        loadCurrentNotes().then(() => {
          // 加载完成后执行回调
          if (typeof callback === 'function') {
            callback()
          }
        })
      })
    }
    
    // 确保编辑器样式生效的辅助函数
    const ensureEditorStyles = () => {
      // 创建一个样式表
      const style = document.createElement('style')
      style.id = 'notes-dialog-editor-styles'
      style.textContent = `
        .editor-content h1 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #409EFF !important;
          margin: 10px 0 !important;
          border-bottom: 1px solid #eee !important;
          padding-bottom: 5px !important;
        }
        .editor-content h2 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #67C23A !important;
          margin: 10px 0 !important;
        }
        .editor-content h3 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #E6A23C !important;
          margin: 10px 0 !important;
        }
        .editor-content p {
          margin: 5px 0 !important;
          font-size: 14px !important;
          color: #333 !important;
        }
        
        /* 兜底样式：必须限定在笔记弹窗内。
           这里原来写的是裸的 h1/h2/h3，又被注入到 document.head，
           等于给全站所有标题加了一条 !important 的 16px + 彩色覆盖，
           落地页等页面的标题会被压成 16px 绿色。 */
        .notes-dialog h1 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #409EFF !important;
          margin: 10px 0 !important;
          border-bottom: 1px solid #eee !important;
          padding-bottom: 5px !important;
        }
        .notes-dialog h2 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #67C23A !important;
          margin: 10px 0 !important;
        }
        .notes-dialog h3 {
          font-size: 16px !important;
          font-weight: bold !important;
          color: #E6A23C !important;
          margin: 10px 0 !important;
        }
      `
      
      // 检查是否已存在，如果存在则替换，否则添加
      const existingStyle = document.getElementById('notes-dialog-editor-styles')
      if (existingStyle) {
        existingStyle.parentNode.replaceChild(style, existingStyle)
      } else {
        document.head.appendChild(style)
      }
    }
    
    // 在组件卸载前清理资源
    onBeforeUnmount(() => {
      // 移除编辑器样式
      const style = document.getElementById('notes-dialog-editor-styles')
      if (style) {
        style.parentNode.removeChild(style)
      }
    })
    
    return {
      dialogVisible,
      title,
      editorRef,
      sidebarCollapsed,
      toggleSidebar,
      handleContentChange,
      handleSelectionChange,
      applyFormat,
      currentFormat,
      saveNotes,
      outlineItems,
      hierarchicalOutline,
      scrollToHeading,
      collapsedHeadings,
      toggleHeading,
      insertText,
      currentContentLength,
      MAX_CONTENT_LENGTH,
      applyHighlight,
      removeHighlight
    }
  }
}
</script>

<style scoped>
/* 自定义标题栏样式 */
.custom-header {
  display: flex;
  align-items: center;
  width: 100%;
  height: 14px;
  padding: 0 8px;
  box-sizing: border-box;
  cursor: move;
  background-color: #fff;
}

.dialog-title {
  font-size: 18px;
  font-weight: bold;
  margin-right: auto;
  color: #303133;
  line-height: 26px;
  height: 26px;
}

.notes-wrapper {
  display: flex;
  gap: 1px;
  height: 380px; /* 减小高度 */
  position: relative;
  margin-bottom: 0;
  overflow: hidden;
}

/* 侧边栏容器与过渡 */
.sidebar-container {
  position: relative;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 150px; /* 增加大纲区域宽度 */
  flex-shrink: 0;
  display: flex;
  height: 100%; /* 确保高度占满父容器 */
}

.sidebar-container.collapsed {
  width: 15px;
}

.sidebar-placeholder {
  width: 15px;
  flex-shrink: 0;
  background-color: #f8f8f8;
  border-right: 1px solid #eee;
  position: relative;
  height: 100%; /* 确保高度占满父容器 */
}

.notes-sidebar {
  width: 100%;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  overflow: hidden; /* 修改为hidden，只让子元素滚动 */
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 折叠/展开按钮样式 */
.sidebar-toggle,
.sidebar-toggle-expanded {
  cursor: pointer;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  transition: all 0.2s;
}

.sidebar-toggle:hover,
.sidebar-toggle-expanded:hover {
  color: #409EFF;
}

.toggle-icon {
  font-size: 12px;
}

/* 在折叠状态下的按钮定位 */
.sidebar-placeholder .sidebar-toggle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* 在展开状态下的按钮定位 */
.sidebar-toggle-expanded {
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #eee;
  z-index: 10;
}

.sidebar-header {
  display: flex;
  justify-content: center; /* 将大纲标题居中 */
  align-items: center;
  padding: 0 0 5px 0;
  margin-bottom: 5px;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0; /* 防止标题被压缩 */
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 5;
}

/* 大纲视图样式 */
.outline-view {
  padding: 5px 0 0 0; /* 移除底部padding */
  flex: 1; /* 让大纲视图占据剩余空间 */
  overflow: auto; /* 允许在两个方向上滚动 */
  min-height: 0; /* 确保flex容器中的内容可以正确缩小 */
  margin-bottom: 0; /* 确保底部没有间距 */
}

.outline-item-container {
  position: relative;
  min-width: min-content; /* 确保容器至少与内容一样宽 */
}

.children-container {
  padding-left: 5px;
  min-width: min-content; /* 确保容器至少与内容一样宽 */
}

.child-container {
  margin-left: 5px;
  min-width: min-content; /* 确保容器至少与内容一样宽 */
}

.outline-item {
  display: flex;
  align-items: center;
  padding: 3px 8px 3px 0;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap; /* 防止文本换行 */
  overflow: visible; /* 允许内容溢出 */
  border-radius: 3px;
  transition: all 0.2s;
  min-width: min-content; /* 确保项目至少与内容一样宽 */
}

.outline-item:hover {
  background-color: #f5f7fa;
}

.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 10px;
  cursor: pointer;
  margin-right: 2px;
  color: #909399;
  flex-shrink: 0; /* 防止图标被压缩 */
}

.expand-icon:hover {
  color: #409EFF;
}

.no-children-space, .indent-space {
  display: inline-block;
  width: 16px;
  flex-shrink: 0; /* 防止空间被压缩 */
}

.item-text {
  flex: 0 1 auto; /* 允许文本压缩但不扩展 */
  overflow: visible; /* 允许文本完全显示 */
  white-space: nowrap; /* 防止文本换行 */
  text-overflow: clip; /* 不要截断文本 */
}

.level-1 {
  font-weight: bold;
  color: #409EFF; /* 蓝色，与编辑区h1一致 */
}

.level-2 {
  color: #67C23A; /* 绿色，与编辑区h2一致 */
}

.level-3 {
  color: #E6A23C; /* 黄色，与编辑区h3一致 */
  padding-left: 10px; /* 减少缩进，从20px改为10px */
  font-size: 11px;
}

.empty-outline {
  flex: 1; /* 让空大纲提示占据剩余空间 */
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0; /* 移除内边距 */
  margin: 0; /* 移除外边距 */
  min-height: 0; /* 允许在flex容器中压缩高度 */
  overflow: hidden; /* 防止内容溢出 */
}

.empty-outline-content {
  position: absolute;
  top: 30%; /* 将提示内容垂直向上移动 */
  left: 0;
  right: 0;
  text-align: center;
  color: #909399;
  font-size: 12px;
}

.outline-tip {
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 5px;
}

.notes-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.format-buttons {
  display: flex;
  gap: 0;
  margin-left: auto;
  height: 14px;
  align-items: center;
}

.format-button {
  font-size: 12px;
  padding: 0 8px;
  border-radius: 0;
  cursor: pointer;
  color: #606266;
  transition: all 0.2s;
  user-select: none;
  height: 14px;
  line-height: 14px;
  text-align: center;
}

.format-button:hover {
  background-color: #f5f7fa;
}

.format-button.active {
  font-weight: bold;
}

.format-button.active[class*="h1"] {
  color: #409EFF;
  background-color: #ecf5ff;
}

.format-button.active[class*="h2"] {
  color: #67C23A;
  background-color: #f0f9eb;
}

.format-button.active[class*="h3"] {
  color: #E6A23C;
  background-color: #fdf6ec;
}

.editor-content {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  font-family: "Microsoft YaHei", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  min-height: 300px;
  outline: none;
}

/* 使用:deep 选择器和强制优先级来确保样式生效 */
:deep(.editor-content h1) {
  font-size: 16px !important;
  font-weight: bold !important;
  color: #409EFF !important;
  margin: 10px 0 !important;
  border-bottom: 1px solid #eee !important;
  padding-bottom: 5px !important;
}

:deep(.editor-content h2) {
  font-size: 16px !important;
  font-weight: bold !important;
  color: #67C23A !important;
  margin: 10px 0 !important;
}

:deep(.editor-content h3) {
  font-size: 16px !important;
  font-weight: bold !important;
  color: #E6A23C !important;
  margin: 10px 0 !important;
}

:deep(.editor-content p) {
  margin: 5px 0 !important;
  font-size: 14px !important;
  color: #333 !important;
}

/* 笔记对话框样式 */
:deep(.notes-dialog .el-dialog__header) {
  padding: 0;
  margin: 0;
  border-bottom: 1px solid #e8e8e8;
  height: 14px;
  line-height: 14px;
  box-sizing: border-box;
}

:deep(.notes-dialog .el-dialog__headerbtn) {
  top: 0px;
  right: 0px;
  z-index: 100;
  height: 14px;
  width: 14px;
  line-height: 14px;
}

:deep(.notes-dialog .el-dialog__headerbtn .el-dialog__close) {
  font-size: 12px;
  line-height: 14px;
}

:deep(.notes-dialog .el-dialog__body) {
  padding: 5px;
  padding-bottom: 0;
  margin-top: 2px;
  margin-bottom: 0; /* 确保底部没有间距 */
  overflow: hidden; /* 防止内容溢出 */
}

:deep(.notes-dialog .el-dialog__footer) {
  padding: 0;
  margin: 0;
  text-align: right;
  border-top: 0;
  height: 20px;
  line-height: 20px;
  box-sizing: border-box;
}

:deep(.notes-dialog .dialog-footer .el-button) {
  padding: 0 8px;
  line-height: 18px;
  height: 18px;
  font-size: 12px;
  margin: 0 5px 0 0;
  position: relative;
  bottom: -1px;
}

:deep(.notes-dialog .dialog-footer .el-button:last-child) {
  margin-right: 5px;
}

/* 底部信息样式 */
:deep(.notes-dialog .dialog-footer) {
  display: flex;
  justify-content: flex-end;
}

/* 确保对话框内容区域占满可用空间 */
:deep(.notes-dialog .el-dialog__body) {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: calc(100% - 34px); /* 减去标题栏和底部栏高度 */
}

:deep(.notes-dialog .el-dialog) {
  display: flex;
  flex-direction: column;
  max-height: 80vh; /* 减小对话框最大高度 */
}

/* 自定义滚动条样式 */
.outline-view::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.outline-view::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.outline-view::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.outline-view::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* 调整底部按钮区域，减少对大纲区域的影响 */
:deep(.notes-dialog .el-dialog__footer) {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  background-color: white;
  z-index: 10;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  overflow: hidden;
}

.content-length-indicator {
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 12px;
  color: #909399;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 10;
}

.content-length-indicator.warning {
  color: #E6A23C;
  font-weight: bold;
}

/* 高亮功能样式 */
.highlight-controls {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.highlight-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.color-box {
  width: 14px;
  height: 14px;
  border-radius: 2px;
  display: inline-block;
}

.color-box.red {
  background-color: #ffcccc;
  border: 1px solid #ff9999;
}

.color-box.yellow {
  background-color: #ffffcc;
  border: 1px solid #ffff99;
}

.color-box.green {
  background-color: #ccffcc;
  border: 1px solid #99ff99;
}

.color-box.clear {
  background-color: #f5f5f5;
  color: #666;
  font-weight: bold;
  text-align: center;
  line-height: 14px;
  font-size: 14px;
  border: 1px solid #ddd;
}

/* 使用:deep来应用到笔记内容中的样式 */
:deep(.highlight-red) {
  background-color: #ffcccc;
}

:deep(.highlight-yellow) {
  background-color: #ffffcc;
}

:deep(.highlight-green) {
  background-color: #ccffcc;
}
</style> 